import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail, CheckCircle2, ArrowRight } from "lucide-react";
import { StudentIcon } from "../lib/icons";
import axios from "axios";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { useNoIndex } from "../lib/useNoIndex";
import { trackBetaSignup } from "../lib/analytics";

/**
 * StudentsComingSoon — the gate students hit when they try to create a
 * campaign before the platform is open to real student submissions.
 *
 * Controlled by REACT_APP_STUDENT_CAMPAIGNS_OPEN in frontend/.env
 * (default `false`). Flip to `true` when a curated cohort is ready.
 *
 * Existing student accounts (e.g. Maya during private review) can still
 * log in via /login — this gate only blocks NEW student registration
 * and campaign creation via the marketing entry points.
 *
 * Design intent: warm, honest, keeps intent from evaporating — student
 * drops their email + school so we can invite them once we open.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function StudentsComingSoon() {
  useNoIndex();
  const [email, setEmail] = useState("");
  const [school, setSchool] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(t);
  }, []);

  const emailValid = useMemo(() => EMAIL_RE.test(email.trim()), [email]);
  const showError = touched && !emailValid && email.length > 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    setTouched(true);
    if (!emailValid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/beta-signups`, {
        email: email.trim().toLowerCase(),
        role: "student",
        source: `student_coming_soon${school ? `:${school.trim()}` : ""}`,
      });
      trackBetaSignup({
        role: "student",
        source: `student_coming_soon${school ? `:${school.trim()}` : ""}`,
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something didn't send. Try again in a moment.");
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="students-coming-soon-page" className="bg-sand">
      <Navbar onStart={() => (window.location.href = "/")} primaryLabel="Back to home" />

      <section className="mx-auto max-w-4xl px-6 pt-36 pb-24 lg:px-8 lg:pt-56">
        <FadeUp tier="sm"><PageAccent className="mb-5" /></FadeUp>

        <FadeUp>
          <span
            className="inline-flex items-center rounded-full bg-ink px-3 py-1 text-[13px] font-semibold uppercase tracking-[0.22em] text-sand"
            data-testid="students-coming-soon-eyebrow"
          >
            Opening soon
          </span>
        </FadeUp>

        <FadeUp>
          <h1
            data-testid="students-coming-soon-headline"
            className="mt-6 font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.98] tracking-tight text-ink"
          >
            Student campaigns open soon.
          </h1>
        </FadeUp>

        <FadeUp>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-olive">
            We&rsquo;re launching with a small first cohort of verified students so every campaign gets the story-craft attention it deserves. Drop your email and school below — you&rsquo;ll be first in line for the next intake.
          </p>
        </FadeUp>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              data-testid="students-coming-soon-success"
              className="mt-10 flex items-start gap-4 rounded-2xl border-2 border-grass/40 bg-grass/[0.06] p-6 lg:p-8"
            >
              <span className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-grass text-sand">
                <CheckCircle2 className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-3xl text-ink">You&rsquo;re on the list.</p>
                <p className="mt-2 text-base leading-relaxed text-olive">
                  We&rsquo;ll email <span className="font-semibold text-ink">{email.trim().toLowerCase()}</span> as soon as our next intake opens.
                </p>
                <Link
                  to="/"
                  data-testid="students-coming-soon-back-home"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
                >
                  Back to home
                </Link>
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              onSubmit={submit}
              className="mt-10 rounded-2xl border-2 border-ink/10 bg-bone p-6 lg:p-8"
              data-testid="students-coming-soon-form"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-tangerine/12 text-tangerine">
                  <StudentIcon className="h-4 w-4" />
                </span>
                <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                  Get notified when the next cohort opens
                </p>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)]">
                <div>
                  <label htmlFor="scs-email" className="sr-only">Email</label>
                  <div className="relative">
                    <span aria-hidden className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-olive/60">
                      <Mail className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <input
                      ref={inputRef}
                      id="scs-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="your.name@example.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setTouched(true)}
                      disabled={submitting}
                      data-testid="students-coming-soon-email"
                      className={`block w-full rounded-full border-2 bg-sand px-11 py-3.5 text-base text-ink placeholder:text-ink/30 focus:outline-none focus:ring-4 focus:ring-ink/10 ${ showError ? "border-scarlet" : "border-border focus:border-ink" }`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="scs-school" className="sr-only">School</label>
                  <input
                    id="scs-school"
                    type="text"
                    placeholder="School (optional)"
                    value={school}
                    onChange={(e) => setSchool(e.target.value)}
                    disabled={submitting}
                    data-testid="students-coming-soon-school"
                    maxLength={80}
                    className="block w-full rounded-full border-2 border-border bg-sand px-5 py-3.5 text-base text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-4 focus:ring-ink/10"
                  />
                </div>
              </div>

              {showError && (
                <p role="alert" data-testid="students-coming-soon-email-error" className="mt-3 text-[13px] text-scarlet">
                  Enter a valid email so we can reach you.
                </p>
              )}
              {error && (
                <p role="alert" data-testid="students-coming-soon-form-error" className="mt-3 rounded-md bg-scarlet/8 px-3 py-2 text-[13px] text-scarlet">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || !emailValid}
                data-testid="students-coming-soon-submit"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Adding you…</>
                ) : (
                  <>Notify me when the next cohort opens <ArrowRight className="h-4 w-4" strokeWidth={2} /></>
                )}
              </button>

              <p className="mt-5 text-[13px] leading-relaxed text-olive">
                Already have an account?{" "}
                <Link to="/login" data-testid="students-coming-soon-login" className="font-semibold text-ink underline decoration-2 underline-offset-2 hover:text-forest">
                  Sign in here
                </Link>.
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </section>

      <Footer
        onStart={() => (window.location.href = "/")}
        onDonate={() => (window.location.href = "/")}
        primaryLabel="Back to home"
        secondaryLabel={null}
      />
    </main>
  );
}
